import React from "react";
import styles from "./divider.module.scss";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import wrapperHOC from "@Utils/wrapperHOC";

export type DividerProps = {
  label?: React.ReactNode;
  labelPosition?: "left" | "right" | "center";
  labelProps?: Record<string, any>;
  orientation?: "horizontal" | "vertical";
  size?: number | "xs" | "sm" | "md" | "lg" | "xl";
  variant?: "dashed" | "dotted" | "solid";
  dividerClassName?: string;
  labelClassName?: string;
  color?: string;
};

const Divider: React.FC<DividerProps> = ({
  label,
  labelPosition = "center",
  labelProps = {},
  orientation = "horizontal",
  size = "md",
  variant = "solid",
  dividerClassName = "",
  labelClassName = "",
  color = "",
}) => {
  const dividerClasses = formatClassNames(
    styles.divider,
    orientation === "horizontal" && styles.horizontal,
    orientation === "vertical" && styles.vertical,
    variant && styles[variant],
    labelPosition && styles[`label-${labelPosition}`],
    dividerClassName,
  );

  const labelClasses = formatClassNames(
    styles.label,
    labelClassName,
    orientation === "vertical" && styles.vertical,
  );

  const sizeStyle = {
    height: orientation === "horizontal" ? getSizeStyle(size) : "10px",
    width: orientation === "vertical" ? getSizeStyle(size) : "100%",
  };

  // Add the color style dynamically
  const colorStyle = color ? { borderColor: color, color } : {};

  return (
    <div className={dividerClasses} style={{ ...sizeStyle, ...colorStyle }}>
      {label && labelPosition === "left" && (
        <span className={labelClasses} style={colorStyle} {...labelProps}>
          {label}
        </span>
      )}
      <div />
      {label && labelPosition !== "left" && (
        <span className={labelClasses} style={colorStyle} {...labelProps}>
          {label}
        </span>
      )}
    </div>
  );
};

const getSizeStyle = (size: number | "xs" | "sm" | "md" | "lg" | "xl") => {
  switch (size) {
    case "xs":
      return "1px";
    case "sm":
      return "2px";
    case "md":
      return "2px";
    case "lg":
      return "3px";
    case "xl":
      return "4px";
    default:
      return size;
  }
};

export default wrapperHOC(Divider, {
  componentName: "Divider",
  showForChina: true,
});
